import ModalWrapper from "..";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import { useReactToPrint, UseReactToPrintOptions } from "react-to-print";
import BulkPrintableBadgesCard from "../../card/BulkPrintableBadgesCard";
import { memo, useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useBulkUpdateBadgePrintStatus } from "../../../hooks/visitor/mutation";
import CheckboxInput from "../../inputs/CheckboxInput";

interface PrintBadgeModalProps {
  appointments: any;
  isOpen: boolean;
  onOpenChange: () => void;
}

const BulkPrintBadgeModal = ({
  appointments,
  isOpen,
  onOpenChange,
}: PrintBadgeModalProps) => {
  const componentRef = useRef(null);

  const { t } = useTranslation();

  const [isPrintBadgeWithoutPicture, setIsPrintBadgeWithoutPicture] =
    useState<boolean>(false);

  const { mutateAsync: BulkUpdateBadgePrintStatus } =
    useBulkUpdateBadgePrintStatus();

  const onClose = useCallback(() => {
    onOpenChange();
    setIsPrintBadgeWithoutPicture(false);
  }, [onOpenChange]);

  const handleOnAfterPrint = useCallback(async () => {
    try {
      const body = appointments.map((item: { n_AppointmentID: number }) => ({
        encryptedId: item?.n_AppointmentID?.toString(),
      }));
      await BulkUpdateBadgePrintStatus(body);
    } catch (error) {
      toast.error(t("somethingWentWrong"));
    } finally {
      onClose();
    }
  }, [BulkUpdateBadgePrintStatus, appointments, onClose, t]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    contentRef: componentRef,
    documentTitle: "Visitors' Badges",
    onAfterPrint: handleOnAfterPrint,
    removeAfterPrint: true,
  } as UseReactToPrintOptions);

  return (
    <>
      <ModalWrapper isOpen={isOpen} onOpenChange={onClose}>
        <div className="flex flex-col items-center justify-center gap-5">
          <div className="flex flex-col items-center justify-center gap-4">
            <p className="text-lg font-medium">
              {t("areYouSureYouWantToPrintBadgesForVisitors")}
            </p>
            <CheckboxInput
              key="print-badge-without-picture"
              id="print-badge-without-picture"
              label={t("printBadgeWithoutPicture")}
              checked={isPrintBadgeWithoutPicture}
              setChecked={() => setIsPrintBadgeWithoutPicture((prev) => !prev)}
            />
          </div>

          <div className="hidden print:block">
            <BulkPrintableBadgesCard
              ref={componentRef}
              appointments={appointments}
              isPrintBadgeWithoutPicture={isPrintBadgeWithoutPicture}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrint}>{t("bulkPrint")}</Button>
            <Button
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              onClick={onClose}
            >
              {t("close")}
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default memo(BulkPrintBadgeModal);
